import { CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Row } from 'reactstrap';
import Toast from '../../helpers/Toast';
import { ClientDTO } from '../../models/Client/ClientDTO';
import { ClientProfitabilityDTO } from '../../models/Profitability/ClientProfitabilityDTO';
import { ClientProfitabilityParametersDTO } from '../../models/Profitability/ClientProfitabilityParametersDTO';
import { ProfitabilityProjectsDTO } from '../../models/Profitability/ProfitabilityProjectsDTO';
import { ClientService } from '../../services/ClientService';
import TableComponent from '../TableComponent';

export type ClientProfitabilityProps = {
    clientId: number;
    reloadExecutions: boolean;
    loadingUpdate: boolean;
}

function ClientProfitabilityTable({ clientId, reloadExecutions, loadingUpdate }: ClientProfitabilityProps) {

    const clientService = new ClientService();
    const pageSizes: number[] = [50, 100];
    const [data, setData] = useState<ProfitabilityProjectsDTO[]>([]);
    const [clientProfitability, setClientProfitability] = useState<ClientProfitabilityDTO>();
    const [pageSize, setPageSize] = useState<number>(50);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [goToPage, setGoToPage] = useState<number>();
    const [pageCount, setPageCount] = useState(1);
    const [reload, setReload] = useState<boolean>(false);
    const intl = useIntl();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingCount, setLoadingCount] = useState<number>(0);

    // Orçamento
    const [isLoadingClient, setIsLoadingClient] = useState<boolean>(false);
    const [client, setClient] = useState<ClientDTO>({} as ClientDTO);


    const columns = React.useMemo(
        () => [
            {
                Header: "Tipo",
                accessor: "transactionType",
                width: 10,
                Cell: (props: any) => {
                    const { value } = props.cell;
                    const isTotal = value === "Total";
                    const cellStyle = isTotal ? { backgroundColor: '#f2f2f2' } : {};
                    return <div style={cellStyle}>{value}</div>;
                },
            },
            {
                Header: "Número Obra",
                accessor: "projectId",
                width: 10,
                Cell: (props: any) => {
                    const { value } = props.cell;
                    const isTotal = props.cell.row.original.transactionType === "Total";
                    const cellStyle = isTotal ? { backgroundColor: '#f2f2f2' } : {};
                    return <div style={cellStyle}>{value}</div>;
                },
            },
            {
                Header: "Documento",
                accessor: "assistances[0].document",
                width: 10,
                Cell: (props: any) => {
                    const { value } = props.cell;
                    const isTotal = props.cell.row.original.transactionType === "Total";
                    const cellStyle = isTotal ? { backgroundColor: '#f2f2f2' } : {};
                    return <div style={cellStyle}>{value}</div>;
                },
            },
            {
                Header: "Total Venda Documento",
                accessor: (row: any) => {
                    const totalLineSellingPrice = row.assistances[0].partsAndHours.reduce((total: number, partAndHour: any) => {
                        return total + partAndHour.totalLineSellingPrice;
                    }, 0);
                    return totalLineSellingPrice;
                },
                width: 10,
                Cell: (props: any) => {
                    const totalLineSellingPrice = props.value;
                    const formattedTotalLineSellingPrice = totalLineSellingPrice ? intl.formatNumber(totalLineSellingPrice, { style: 'currency', currency: 'EUR' }) : '-';
                    const isTotal = props.cell.row.original.transactionType === "Total";
                    const cellStyle = isTotal ? { backgroundColor: '#f2f2f2' } : {};
                    return <div className="table_align-right" style={cellStyle}>{formattedTotalLineSellingPrice}</div>;
                },
            },
            {
                Header: "Total Custo Documento",
                accessor: (row: any) => {
                    const totalLineCostPrice = row.assistances.reduce((total: number, assistanceLine: any) => {
                        const lineCostPrice = assistanceLine.partsAndHours.reduce((lineTotal: number, partAndHour: any) => {
                            return lineTotal + partAndHour.totalLineCostPrice;
                        }, 0);
                        return total + lineCostPrice;
                    }, 0);
                    return totalLineCostPrice;
                },
                width: 10,
                Cell: (props: any) => {
                    const totalLineCostPrice = props.cell.value;
                    const formattedTotalLineCostPrice = totalLineCostPrice ? intl.formatNumber(totalLineCostPrice, { style: 'currency', currency: 'EUR' }) : '-';
                    const isTotal = props.cell.row.original.transactionType === "Total";
                    const cellStyle = isTotal ? { backgroundColor: '#f2f2f2' } : {};
                    return <div className="table_align-right" style={cellStyle}>{formattedTotalLineCostPrice}</div>;
                },
            },
            {
                Header: "Total Lucro Documento",
                accessor: (row: any) => {
                    const totalLineProfit = row.totalProjectProfit;
                    const formattedTotalLineProfit = totalLineProfit ? intl.formatNumber(totalLineProfit, { style: 'currency', currency: 'EUR' }) : '-';
                    const isTotal = row.transactionType === "Total";
                    const cellStyle = isTotal ? { backgroundColor: '#f2f2f2' } : {};
                    return <div className="table_align-right" style={cellStyle}>{formattedTotalLineProfit}</div>;
                },
                width: 10,
            },
        ],
        [],
    );

    const fetchData = async () => {
        setLoading(true);

        const profitabilityParams: ClientProfitabilityParametersDTO = {
            clientId: Number(clientId) ?? 0,
            showBillabledDocuments: true
        };

        const response = await clientService.GetClientProfitability(profitabilityParams);

        if (response.success === false && response.obj === null) {
            setData([]);
            Toast.Show("error", response.message);
            setLoading(false);
            setLoadingCount(1);
            return;
        }

        const newData: ProfitabilityProjectsDTO[] = [];

        response.obj.projects.forEach((clientLine) => {
            clientLine.assistances.forEach((projectLine) => {
                newData.push({
                    transactionType: projectLine.transactionType,
                    projectId: clientLine.projectId,
                    assistances: [projectLine],
                    totalProjectProfit: projectLine.totalAssistanceProfit // Usar o lucro total do projeto fornecido pela API
                });
            });

            // Adicionar linha "Total" após cada conjunto de linhas do projeto
            newData.push({
                transactionType: "Total",
                projectId: "-",
                assistances: [{
                    transactionType: "",
                    document: "-",
                    partsAndHours: [],
                    totalAssistanceProfit: clientLine.totalProjectProfit
                }],
                totalProjectProfit: clientLine.totalProjectProfit // Passar o lucro total do projeto para a linha "Total"
            });
        });

        setData(newData);
        setClientProfitability(response.obj);

        setLoading(false);
        setLoadingCount(1);
    };


    useEffect(() => {
        if (reloadExecutions || loadingUpdate) {
            fetchData();
            setReload(false);
        }
    }, [reloadExecutions, loadingUpdate]);

    return (
        <div>
            <Row style={{ margin: "1rem 0px" }}>
                <h5><FormattedMessage id={"Screens.Projects.Profitability.Title"}></FormattedMessage></h5>
                <br></br>
                <br></br>

                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <div>
                        <p style={{ marginRight: '1.5rem' }}>
                            <strong>
                                <FormattedMessage id={"Screens.Projects.Profitability.Real"} />
                            </strong>{" "}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Projects.Profitability.TotalSellingPrice"} />
                            {intl.formatNumber(clientProfitability?.totalClientSellingPrice ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Projects.Profitability.TotalCostPrice"} />
                            {intl.formatNumber(clientProfitability?.totalClientCostPrice ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Projects.Profitability.TotalProfit"} />
                            {intl.formatNumber(clientProfitability?.totalClientProfit ?? 0, { style: 'currency', currency: 'EUR' })}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Projects.Profitability.RealSellingMargin"} />
                            {intl.formatNumber((clientProfitability?.realSellingMargin ?? 0) / 100, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        </p>
                        <p style={{ marginRight: '1.5rem' }}>
                            <FormattedMessage id={"Screens.Projects.Profitability.RealCostMargin"} />
                            {intl.formatNumber((clientProfitability?.realCostMargin ?? 0) / 100, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        </p>
                    </div>
                </div>
                <br></br>
                <TableComponent
                    columns={columns}
                    data={data}
                    goToPage={goToPage}
                    initialPage={currentPage}
                    pageCount={pageCount}
                    initialPageSize={pageSize}
                    pageSizes={pageSizes}
                    manualPagination={true}
                    loading={isLoading}
                    fetchData={fetchData}
                    initialSortBy={[]}
                    disableSortBy={true}
                />
            </Row>
            {loading === true && loadingCount !== 1 && (
                <div className="loading">
                    <span>
                        {" "}
                        <FormattedMessage id="Common.Loading.DataLoading" />
                        ...{" "}
                    </span>
                    <CircularProgress style={{ marginLeft: "10px" }} size={"25px"} />
                </div>
            )}
        </div>
    )
}

export default ClientProfitabilityTable